/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useEffect, useState } from 'react';
import {
  fetchDashboardPreferences,
  saveOverviewMode,
  saveOverviewTiles,
  type OverviewMode,
  type OverviewTiles,
  type OverviewView,
} from '@/services/housien/dashboard';
import { NO_CHOICE } from './Overview.catalogue';

interface UseDashboardPreferencesResult {
  /** How much of the overview this person wants on screen. */
  mode: OverviewMode;
  /** The cards chosen per view and mode; empty lists mean "never chose". */
  tiles: OverviewTiles;
  isLoading: boolean;
  /** Set when a save was rejected, so the page can say so out loud. */
  saveError: boolean;
  setMode: (mode: OverviewMode) => void;
  /** Replace one view-and-mode list; `[]` hands it back to the default. */
  setTiles: (view: OverviewView, mode: OverviewMode, ids: string[]) => void;
}

/**
 * The screen somebody sees before they have chosen one.
 *
 * Advanced in both directions -- for a stored document written before the
 * choice existed, and for a value this build does not recognise. Falling
 * back to more costs a scroll; falling back to less would hide numbers
 * somebody was relying on.
 */
const DEFAULT_MODE: OverviewMode = 'advanced';

/** The setters the load effect writes through, once the response lands. */
interface PreferenceSetters {
  setTiles: (tiles: OverviewTiles) => void;
  setMode: (mode: OverviewMode) => void;
  setIsLoading: (loading: boolean) => void;
}

/**
 * Fetches the stored preferences and writes them through, dropping the
 * result when the effect has already been torn down. A preference is not
 * the page: a failed read falls back to the defaults and carries on.
 */
function loadPreferences(
  agencyId: string,
  alive: { current: boolean },
  setters: PreferenceSetters,
): void {
  fetchDashboardPreferences(agencyId)
    .then((preferences) => {
      if (!alive.current) return;
      setters.setTiles(preferences.tiles ?? NO_CHOICE);
      setters.setMode(preferences.mode ?? DEFAULT_MODE);
    })
    .catch(() => undefined)
    .finally(() => {
      if (alive.current) setters.setIsLoading(false);
    });
}

/**
 * Loads and persists the user's own overview preferences: the mode, and
 * which cards each mode shows.
 *
 * Writes are optimistic: switching a card on should change the screen, not
 * submit a form, so the board reflects the change immediately and the
 * request follows. A rejected save rolls back to what the server still
 * believes and raises `saveError` -- silently keeping a choice that was
 * not stored would be worse than losing the gesture, because the next
 * reload would undo it with no explanation.
 *
 * @param agencyId - Agency whose preferences are being read and written
 * @returns The mode, the cards, and the mutators
 */
export function useDashboardPreferences(
  agencyId: string | undefined,
): UseDashboardPreferencesResult {
  const [tiles, setTilesState] = useState<OverviewTiles>(NO_CHOICE);
  const [mode, setModeState] = useState<OverviewMode>(DEFAULT_MODE);
  const [isLoading, setIsLoading] = useState(true);
  const [saveError, setSaveError] = useState(false);

  useEffect(() => {
    if (!agencyId) {
      setIsLoading(false);
      return;
    }
    const alive = { current: true };
    setIsLoading(true);
    loadPreferences(agencyId, alive, {
      setTiles: setTilesState, setMode: setModeState, setIsLoading,
    });
    return () => {
      alive.current = false;
    };
  }, [agencyId]);

  const setMode = useCallback(
    (next: OverviewMode) => {
      const previous = mode;
      setModeState(next);
      setSaveError(false);
      if (!agencyId) return;
      saveOverviewMode(agencyId, next)
        .then((saved) => setModeState(saved.mode ?? next))
        .catch(() => {
          setModeState(previous);
          setSaveError(true);
        });
    },
    [agencyId, mode],
  );

  const setTiles = useCallback(
    (view: OverviewView, forMode: OverviewMode, ids: string[]) => {
      const previous = tiles;
      const next: OverviewTiles = {
        ...tiles,
        [view]: { ...tiles[view], [forMode]: ids },
      };
      setTilesState(next);
      setSaveError(false);
      if (!agencyId) return;
      // The answer is not written back: toggling three cards quickly puts
      // three saves in flight, and an earlier one landing last would undo
      // the later clicks on screen. The server stores what was sent.
      saveOverviewTiles(agencyId, next)
        .catch(() => {
          setTilesState(previous);
          setSaveError(true);
        });
    },
    [agencyId, tiles],
  );

  return {
    mode, tiles, isLoading, saveError, setMode, setTiles,
  };
}
